import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { CardInfo as SearchCardInfo } from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/components/CardInfo/CardInfo';
import { CardInfo as TimedPlusCardInfo } from '@/components/GridItemCard/Card/CardOptions/TimedPlusItemCard/components/CardInfo/CardInfo';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import {
    getIsItemSummaryTimedPlusBiddingOpen,
    getItemSummaryIsBuyNowOnly,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';

export type DateAndHouseComponentProps = ItemSummary & {
    hasNewSimilarItemsIconCTA?: boolean;
    position: number;
};

export const DateAndHouseComponent = createInfo<DateAndHouseComponentProps>({
    className: 'line-clamp-0',
    useText: ({
        hasNewSimilarItemsIconCTA = false,
        isPassed,
        isSold,
        isTimedPlusAuction,
        itemId,
        position,
        promoted,
    }) => {
        const { onNavigateInitiated } = useItemCardNavigation(itemId);

        const isMobile = useIsMobileLayout();
        const isTimedPlusBiddingOpen = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingOpen(state, itemId));
        const isBuyNowOnly = useAppSelector((state) => getItemSummaryIsBuyNowOnly(state, itemId));
        const isOpenItem = !isPassed && !isSold && isTimedPlusBiddingOpen;

        // Experiment: sear-1971-search-tile-live-lot-position. Exposure fires once per page from the
        // SearchPage/CategorySearchPage mount — read the resolved variant here instead of re-tracking it per card.
        const { variants } = useAmplitudeExperiments();
        const isLiveLotPositionTreatment =
            variants[AmplitudeExperiments.SearchTileLiveLotPosition]?.value ===
            AmplitudeVariants.SearchTileLiveLotPosition.Treatment;

        return (
            <div
                className={cn('mt-xs', {
                    'px-xs pb-2xs smMin:px-sm': hasNewSimilarItemsIconCTA,
                })}
            >
                {isTimedPlusAuction && !isBuyNowOnly ? (
                    <TimedPlusCardInfo
                        hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                        isMobile={isMobile}
                        isOpenItem={isOpenItem}
                        itemId={itemId}
                        onNavigate={(trigger) => {
                            onNavigateInitiated(trigger, { position, promoted });
                        }}
                    />
                ) : (
                    <SearchCardInfo
                        className={cn({
                            'pb-xs text-xs smMin:text-sm': hasNewSimilarItemsIconCTA,
                        })}
                        hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                        isLiveLotPositionTreatment={isLiveLotPositionTreatment}
                        itemId={itemId}
                        onNavigate={(trigger) => {
                            onNavigateInitiated(trigger, { position, promoted });
                        }}
                    />
                )}
            </div>
        );
    },
});
